import {
  AvailabilityRule,
  DayOfWeek,
} from '../../../../../calendar/api/availability';
import { StaffBooking } from '../../types';

const ORDER: DayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const SHORT: Record<DayOfWeek, string> = {
  MONDAY: 'Mon',
  TUESDAY: 'Tue',
  WEDNESDAY: 'Wed',
  THURSDAY: 'Thu',
  FRIDAY: 'Fri',
  SATURDAY: 'Sat',
  SUNDAY: 'Sun',
};

/** "Mon–Fri" for a run of three or more consecutive days, otherwise "Mon, Wed". */
const dayLabel = (days: DayOfWeek[]): string => {
  const idx = days.map((d) => ORDER.indexOf(d)).sort((a, b) => a - b);
  const parts: string[] = [];
  let i = 0;
  while (i < idx.length) {
    let j = i;
    while (j + 1 < idx.length && idx[j + 1] === idx[j] + 1) j += 1;
    if (j - i >= 2) {
      parts.push(`${SHORT[ORDER[idx[i]]]}–${SHORT[ORDER[idx[j]]]}`);
    } else {
      for (let k = i; k <= j; k += 1) parts.push(SHORT[ORDER[idx[k]]]);
    }
    i = j + 1;
  }
  return parts.join(', ');
};

/**
 * One line per distinct window: the days that share it, the hours, the zone and the
 * slot length. Rules for the same hours on several days collapse into one line.
 */
export const summariseAvailability = (rules: AvailabilityRule[]): string[] => {
  const groups = new Map<
    string,
    { days: DayOfWeek[]; rule: AvailabilityRule }
  >();
  rules.forEach((rule) => {
    const key = [
      rule.startTime,
      rule.endTime,
      rule.timezone,
      rule.slotDurationMinutes,
    ].join('|');
    const group = groups.get(key);
    if (group) group.days.push(rule.dayOfWeek);
    else groups.set(key, { days: [rule.dayOfWeek], rule });
  });
  return Array.from(groups.values())
    .sort(
      (a, b) =>
        Math.min(...a.days.map((d) => ORDER.indexOf(d))) -
        Math.min(...b.days.map((d) => ORDER.indexOf(d))),
    )
    .map(
      ({ days, rule }) =>
        `${dayLabel(days)} ${rule.startTime}–${rule.endTime} (${rule.timezone}), ${rule.slotDurationMinutes}-minute slots`,
    );
};

/**
 * "Tue, Oct 7, 9:00 AM PDT". Shown in the user's chosen display zone when they have set one in
 * Preferences, otherwise in the staff member's own zone from the booking. A zone the browser
 * does not know falls through to the next choice and finally to UTC, labelled as such, rather
 * than showing a time in the wrong zone unlabelled.
 */
export const formatBookingStart = (
  booking: Pick<StaffBooking, 'startAt' | 'timezone'>,
  displayTimezone?: string | null,
): string => {
  const date = new Date(booking.startAt);
  if (Number.isNaN(date.getTime())) return '—';
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  };
  const candidates = [displayTimezone, booking.timezone, 'UTC'];
  for (const timeZone of candidates) {
    if (!timeZone) continue;
    try {
      return new Intl.DateTimeFormat('en-US', { ...options, timeZone }).format(
        date,
      );
    } catch {
      // An unknown zone: try the next choice.
    }
  }
  return '—';
};
